import React, { useEffect, useState } from 'react';
import { useAuth } from '../app/modules/auth';
import { Navbar } from '../_metronic/layout/components/header/Navbar';

interface AccessControlProps {
    children: React.ReactNode;
}

interface User {
    Id: number;
    Name: string;
    Email: string;
    IdProfile: number;
    IdOrganization: number | null;
    Password: string | null;
    ChangePasswordCode: string | null;
    Salt: string | null;
    FirstLogin: boolean;
}

const AccessControl: React.FC<AccessControlProps> = ({ children }) => {
    const { auth } = useAuth();
    const user = auth?.User;
    const emailUsuario = user?.Email;

    const [hasAccess, setHasAccess] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const checkUserAccess = async () => {
            if (!emailUsuario) {
                setHasAccess(false);
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setError(null);

                const backendUrl = process.env.REACT_APP_BACKEND_URL;
                if (!backendUrl) {
                    throw new Error('URL do backend não configurada');
                }

                const response = await fetch(`${backendUrl}/api/User`);

                if (!response.ok) {
                    throw new Error(`Erro ao buscar lista de usuários: ${response.status}`);
                }

                const users: User[] = await response.json();

                // Verifica se o email do usuário está na lista
                const userExists = users.some(user => user.Email === emailUsuario);

                console.log(`Verificação de acesso para ${emailUsuario}: ${userExists ? 'PERMITIDO' : 'NEGADO'}`);
                console.log(`Total de usuários na lista: ${users.length}`);

                setHasAccess(userExists);
            } catch (err) {
                console.error('Erro ao verificar acesso:', err);
                setError(err instanceof Error ? err.message : 'Erro desconhecido');
                setHasAccess(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkUserAccess();
    }, [emailUsuario]);

    // Mostra loading enquanto verifica
    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div className="ui loading spinner icon" style={{ fontSize: '2rem' }}></div>
                <div>Verificando acesso...</div>
            </div>
        );
    }

    // Se não tem acesso, mostra página de acesso negado
    if (hasAccess === false) {
        return <AccessDeniedPage email={emailUsuario} error={error} />;
    }

    // Se tem acesso, renderiza os children
    return <>{children}</>;
};

// Componente da página de acesso negado
const AccessDeniedPage: React.FC<{ email?: string; error?: string | null }> = ({ email, error }) => {
    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100vh',
            backgroundColor: '#f8f9fa'
        }}>
            {/* Header com navbar */}
            <div style={{
                backgroundColor: 'white',
                borderBottom: '1px solid #e9ecef',
                padding: '0 20px',
                height: '70px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px'
                }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        backgroundColor: '#dc3545',
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '1.2rem'
                    }}>
                        🚫
                    </div>
                    <div>
                        <h3 style={{ margin: 0, color: '#495057' }}>Portal Taggen</h3>
                        <small style={{ color: '#6c757d' }}>Acesso Negado</small>
                    </div>
                </div>

                {/* Navbar com opção de logout */}
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Navbar />
                </div>
            </div>

            {/* Conteúdo da página de acesso negado */}
            <div style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '20px',
                textAlign: 'center'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    padding: '40px',
                    borderRadius: '10px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    maxWidth: '500px',
                    width: '100%'
                }}>
                    <div style={{ fontSize: '4rem', marginBottom: '20px' }}>🚫</div>
                    <h1 style={{
                        color: '#dc3545',
                        marginBottom: '20px',
                        fontSize: '2rem'
                    }}>
                        Acesso Negado
                    </h1>

                    <p style={{
                        fontSize: '1.1rem',
                        marginBottom: '15px',
                        color: '#6c757d'
                    }}>
                        Você não tem permissão para acessar este sistema.
                    </p>

                    {email && (
                        <p style={{
                            fontSize: '1rem',
                            marginBottom: '15px',
                            color: '#495057',
                            backgroundColor: '#e9ecef',
                            padding: '10px',
                            borderRadius: '5px'
                        }}>
                            <strong>Email:</strong> {email}
                        </p>
                    )}

                    {error && (
                        <p style={{
                            fontSize: '0.9rem',
                            marginBottom: '15px',
                            color: '#dc3545',
                            backgroundColor: '#f8d7da',
                            padding: '10px',
                            borderRadius: '5px',
                            border: '1px solid #f5c6cb'
                        }}>
                            <strong>Erro:</strong> {error}
                        </p>
                    )}

                    <p style={{
                        fontSize: '0.9rem',
                        color: '#6c757d',
                        marginTop: '20px'
                    }}>
                        Entre em contato com o administrador do sistema para solicitar acesso:
                        <a href="mailto:contato@taggen.com.br">suporte@taggen.com.br</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export { AccessControl };
